/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './notification.module.scss';
import { Divider, IconButton, Menu, MenuItem, MenuList, SvgIcon } from '@mui/material';
import ObrasAPI from '../../services/APIObras';

import { ReactComponent as notificationReadIcon } from '../../assets/notification_read.svg';
import { ReactComponent as notificationPendingIcon } from '../../assets/notification_pending.svg';
import { AuthContext } from '../../services/authContextProvider';
import { ReactComponent as tab_error } from '../../assets/tab_error.svg';
import { useNavigate } from 'react-router-dom';
import { Motivo, NotificationPage, NotificationType } from '../../types/Notification';
import parse, { Element } from 'html-react-parser';
import { NotificationContext } from '../../context/NotificationProvider';
import CheckSaved from '../checkSaved/checkSaved';
import { SaveContext } from '../../context/SaveProvider';
import { UserModeContext } from '../../context/UserModeProvider';

const DAYS_AGE = 30; // to return only notifications newer than this number of days

function Notification() {
  const { t } = useTranslation();

  const api = ObrasAPI.getInstance();
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();
  const newNotification = useContext(NotificationContext);
  const saveContext = useContext(SaveContext);
  const userModeContext = useContext(UserModeContext);
  const isAuthorized = authContext.canSystem('can_save_any_project') || authContext.canSystem('can_validate_any_project');
  const [notifications, setNotifications] = useState<NotificationType[]>([]);
  const [unreadNotifications, setUnreadNotifications] = useState<boolean>(false);
  const [hasChangedForm, setHasChangedform] = useState<boolean>(false);
  const [route, setRoute] = useState<string>('/notificaciones');
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const requestRef = useRef<AbortController | null>(null);
  const lastLoadedAtRef = useRef(0);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const refreshQueuedRef = useRef(false);

  const continueEdit = () => {
    saveContext.saveDraft = false;
    saveContext.handleChangeSave(false);
    setHasChangedform(false);
    navigate(route);
  };

  const discardChanges = () => {
    saveContext.saveDraft = false;
    saveContext.handleChangeSave(false);
    setHasChangedform(false);
  };

  const loadNotifications = useCallback(async (cursor?: string, append = false) => {
    if (!isAuthorized) return;
    if (requestRef.current) {
      if (!append) refreshQueuedRef.current = true;
      return;
    }

    const controller = new AbortController();
    requestRef.current = controller;
    setLoading(true);
    setLoadError(false);
    try {
      const response = await api.getNotificationsAPI(DAYS_AGE, cursor, controller.signal);
      if (controller.signal.aborted) return;
      const page: NotificationPage = Array.isArray(response)
        ? { data: response as unknown as NotificationType[], pageSize: response.length, hasNextPage: false, nextCursor: null }
        : response;
      setNotifications((previous) => {
        const merged = append ? [...previous, ...page.data] : page.data;
        const unique = new Map(merged.map((notification) => [notification.id, notification]));
        return [...unique.values()];
      });
      setUnreadNotifications((previous) => append
        ? previous || page.data.some((notification) => !notification.leido)
        : page.data.some((notification) => !notification.leido));
      setNextCursor(page.nextCursor);
      setHasNextPage(page.hasNextPage);
      lastLoadedAtRef.current = Date.now();
    } catch (error) {
      if (!controller.signal.aborted) {
        console.error(error);
        setLoadError(true);
      }
    } finally {
      if (requestRef.current === controller) requestRef.current = null;
      setLoading(false);
      if (refreshQueuedRef.current && !controller.signal.aborted) {
        refreshQueuedRef.current = false;
        refreshTimerRef.current = setTimeout(() => {
          refreshTimerRef.current = undefined;
          void loadNotifications();
        }, 150);
      }
    }
  }, [api, isAuthorized]);

  const scheduleRefresh = useCallback(() => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = setTimeout(() => {
      refreshTimerRef.current = undefined;
      void loadNotifications();
    }, 150);
  }, [loadNotifications]);


  useEffect(() => {
    if (isAuthorized) {
      scheduleRefresh();
    }
  }, [authContext.systemCapabilitiesLoaded, isAuthorized, newNotification, scheduleRefresh]);

  useEffect(() => () => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    requestRef.current?.abort();
  }, []);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!requestRef.current && Date.now() - lastLoadedAtRef.current > 30_000) scheduleRefresh();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNavigation = (routeToNavigate: string) => {
    // Check if the user is in editor mode and draft is being saved
    if (userModeContext.userMode === 'editor' && saveContext.saveDraft) {
      // If true, set the flag to show the dialog to continue or discard changes
      setHasChangedform(true);
    } else {
      // If false, navigate to the specified route
      navigate(routeToNavigate);
    }
  };

  const handleGoNotification = async (n: NotificationType) => {
    handleClose();
    api.setNotificationRead(n).then(() => {
      if (
        n.motivo === Motivo.NEW_SUPERCENTER_EDITOR ||
        n.motivo === Motivo.NEW_SUPERCENTER_VALIDATOR ||
        n.motivo === Motivo.NEW_ZONE_EDITOR ||
        n.motivo === Motivo.NEW_ZONE_VALIDATOR
      ) {
        setRoute('/acciones-pendientes');
        handleNavigation('/acciones-pendientes');
      } else if (n.motivo === Motivo.REJECT) {
        setRoute('/acciones-pendientes?filter=COMMENTED');
        handleNavigation('/acciones-pendientes?filter=COMMENTED');
      } else {
        setRoute(`/${n.codCentro}/detalles`);
        handleNavigation(`/${n.codCentro}/detalles`);
      }
    });
  };

  const handleAll = () => {
    setRoute('/acciones-pendientes');
    handleNavigation('/acciones-pendientes');
    handleClose();
  };

  const handleHistory = () => {
    setRoute('/notificaciones');
    handleNavigation('/notificaciones');
    handleClose();
  };

  const handleNotificationListScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const list = event.currentTarget;
    const isNearBottom = list.scrollTop + list.clientHeight >= list.scrollHeight - 24;
    if (isNearBottom && hasNextPage && nextCursor && !loading) {
      void loadNotifications(nextCursor, true);
    }
  };

  const parseDate = (date: string) => {
    const d = new Date(date);
    const options: any = { year: 'numeric', month: 'short', day: 'numeric' };
    return `${d.toLocaleDateString('es-es', options)} - ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  };

  const getNotificationComponent = (not: NotificationType) => {
    return <div className={styles.notificationItem}>
      <div className={styles.notificationPoint}>
        {!not.leido && <SvgIcon component={tab_error} inheritViewBox />}
      </div>
      <div className={styles.notificationData}>
        <div className={styles.notificationName}>
          {`${not.nomCentro} - ${not.codCentro}`}
        </div>
        <div className={styles.notificationMotive}>
          {parse(t('notifications.' + not.motivo),
            {
              replace: (domNode) => {
                if (domNode instanceof Element && domNode.attribs) {
                  if (domNode.attribs && domNode.attribs.id === 'replace') {
                    return <span>{not.nomCentro}</span>;
                  }
                }
              }
            })
          }
        </div>
        <div className={styles.notificationDate}>
          {parseDate(not.fecCreacion)}
        </div>
      </div>
    </div >;
  };

  return isAuthorized ? (
    <>
      <div data-testid='notification-test' className={styles.notificationContainer}>
        <IconButton
          className={styles.notificationButton}
          aria-label={String(t('notifications.title'))}
          onClick={handleClick}>
          <SvgIcon component={unreadNotifications ? notificationPendingIcon : notificationReadIcon} inheritViewBox />
        </IconButton>

        <Menu
          id='notification-menu'
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
          sx={{
            '& .MuiPaper-root': {
              borderRadius: '3px !important'
            },
            '& .MuiMenu-list': {
              padding: 0
            },
          }}
        >
          <MenuList dense
            className={styles.menuList}
            sx={{
              padding: 0
            }}
          >
            <MenuItem className={styles.menuHeader} onClick={handleAll}>
              <div className={styles.headerTitle}>
                {t('notifications.title')}
              </div>
              <div>
                {<IconButton
                  id='notifications-all'
                  className={styles.all}
                  disableRipple={true}
                >
                  {t('notifications.all')}
                </IconButton>}
              </div>
            </MenuItem>
            <Divider sx={{
              margin: '0 !important'
            }} />
            {notifications?.length ?
              <div className={styles.notificationList} onScroll={handleNotificationListScroll}>
                {notifications.map((n, i) => {
                  return <>
                    <div
                      key={n.id}
                      onClick={() => handleGoNotification(n)}>
                      {getNotificationComponent(n)}
                    </div>
                    {i < notifications.length - 1 && <Divider sx={{
                      margin: '0 !important'
                    }} />}
                  </>;
                })
                }
              </div>
              : <div className={styles.noData}>
                {loading ? t('notifications.loading') : loadError ? t('notifications.loadError') : t('notifications.noData')}
              </div>
            }
            {loadError && <MenuItem onClick={() => void loadNotifications()}>{t('notifications.retry')}</MenuItem>}
            <MenuItem className={styles.history} onClick={handleHistory}>
              {t('notifications.history')}
            </MenuItem>
          </MenuList>

        </Menu>
      </div>
      {
        (hasChangedForm) ?
          <CheckSaved
            handleFunction={() => {
              continueEdit();
            }}
            closeFunction={() => {
              setHasChangedform(false);
              handleClose();
            }}
            discardChanges={() => {
              discardChanges();
            }}
          />
          : null
      }
    </>
  ) : <></>;
}

export default Notification;
